import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import monitoring from '../services/monitoring';

/**
 * Hook to automatically track page views
 */
export function usePageTracking() {
  const location = useLocation();
  const previousPath = useRef<string>('');

  useEffect(() => {
    const currentPath = location.pathname + location.search;

    // Only track if the path has actually changed
    if (currentPath !== previousPath.current) {
      monitoring.trackPageView(currentPath);
      previousPath.current = currentPath;
    }
  }, [location]);
}

/**
 * Hook for tracking user interactions
 */
export function useAnalytics() {
  const trackEvent = useCallback(
    (
      action: string,
      category: string,
      label?: string,
      value?: number,
      customParameters?: Record<string, unknown>
    ) => {
      monitoring.trackEvent({
        action,
        category,
        label,
        value,
        custom_parameters: customParameters,
      });
    },
    []
  );

  const trackClick = useCallback(
    (element: string, additionalData?: Record<string, unknown>) => {
      monitoring.trackUserInteraction(element, 'click', additionalData);
    },
    []
  );

  const trackFormSubmission = useCallback(
    (
      formName: string,
      success: boolean = true,
      additionalData?: Record<string, unknown>
    ) => {
      monitoring.trackEvent({
        action: 'form_submit',
        category: 'Form',
        label: formName,
        custom_parameters: {
          success,
          ...additionalData,
        },
      });
    },
    []
  );

  const trackSearch = useCallback(
    (
      query: string,
      results?: number,
      additionalData?: Record<string, unknown>
    ) => {
      monitoring.trackEvent({
        action: 'search',
        category: 'Search',
        label: query,
        value: results,
        custom_parameters: additionalData,
      });
    },
    []
  );

  const trackDownload = useCallback(
    (
      fileName: string,
      fileType?: string,
      additionalData?: Record<string, unknown>
    ) => {
      monitoring.trackEvent({
        action: 'download',
        category: 'File',
        label: fileName,
        custom_parameters: {
          file_type: fileType,
          ...additionalData,
        },
      });
    },
    []
  );

  const trackTiming = useCallback(
    (
      name: string,
      duration: number,
      category: string = 'Performance',
      additionalData?: Record<string, unknown>
    ) => {
      monitoring.trackEvent({
        action: 'timing',
        category,
        label: name,
        value: Math.round(duration),
        custom_parameters: additionalData,
      });
    },
    []
  );

  return {
    trackEvent,
    trackClick,
    trackFormSubmission,
    trackSearch,
    trackDownload,
    trackTiming,
  };
}

/**
 * Hook for performance monitoring
 */
export function usePerformanceMonitoring() {
  const { trackTiming } = useAnalytics();

  const measurePerformance = useCallback(
    (name: string, fn: () => Promise<unknown> | unknown) => {
      const start = performance.now();

      const handleResult = (result: unknown) => {
        const duration = performance.now() - start;
        trackTiming(name, duration, 'Custom Performance');
        return result;
      };

      try {
        const result = fn();

        if (result && typeof (result as Promise<unknown>).then === 'function') {
          // Handle async function
          return (result as Promise<unknown>)
            .then(handleResult)
            .catch((error: Error) => {
              const duration = performance.now() - start;
              trackTiming(`${name}_error`, duration, 'Custom Performance');
              throw error;
            });
        } else {
          // Handle sync function
          return handleResult(result);
        }
      } catch (error) {
        const duration = performance.now() - start;
        trackTiming(`${name}_error`, duration, 'Custom Performance');
        throw error;
      }
    },
    [trackTiming]
  );

  const startTimer = useCallback(
    (name: string) => {
      const start = performance.now();

      return {
        stop: () => {
          const duration = performance.now() - start;
          trackTiming(name, duration, 'Custom Performance');
          return duration;
        },
      };
    },
    [trackTiming]
  );

  return {
    measurePerformance,
    startTimer,
  };
}

/**
 * Hook for A/B testing and feature flags
 */
export function useExperiments() {
  const { trackEvent } = useAnalytics();

  const trackExperiment = useCallback(
    (
      experimentName: string,
      variant: string,
      additionalData?: Record<string, unknown>
    ) => {
      trackEvent('experiment_view', 'A/B Test', experimentName, undefined, {
        variant,
        ...additionalData,
      });
    },
    [trackEvent]
  );

  const trackConversion = useCallback(
    (
      experimentName: string,
      variant: string,
      conversionType: string,
      additionalData?: Record<string, unknown>
    ) => {
      trackEvent(
        'experiment_conversion',
        'A/B Test',
        experimentName,
        undefined,
        {
          variant,
          conversion_type: conversionType,
          ...additionalData,
        }
      );
    },
    [trackEvent]
  );

  return {
    trackExperiment,
    trackConversion,
  };
}

/**
 * Hook for error handling in functional components
 */
export function useErrorTracking() {
  const captureError = useCallback(
    (
      error: Error | string,
      severity: 'low' | 'medium' | 'high' | 'critical' = 'medium',
      additionalData?: Record<string, unknown>
    ) => {
      const errorMessage = typeof error === 'string' ? error : error.message;
      const errorStack = typeof error === 'string' ? undefined : error.stack;

      monitoring.captureError({
        message: errorMessage,
        stack: errorStack,
        severity,
        tags: {
          type: 'hook_error',
          component: 'useErrorTracking',
        },
        extra: additionalData,
      });
    },
    []
  );

  const captureException = useCallback(
    (error: Error, context?: Record<string, unknown>) => {
      captureError(error, 'high', context);
    },
    [captureError]
  );

  const addBreadcrumb = useCallback(
    (
      message: string,
      category: string = 'navigation',
      data?: Record<string, unknown>
    ) => {
      monitoring.addBreadcrumb(message, category, data);
    },
    []
  );

  return {
    captureError,
    captureException,
    addBreadcrumb,
  };
}

/**
 * Hook for user context management
 */
export function useUserTracking() {
  const setUser = useCallback(
    (userId: string, email?: string, username?: string) => {
      monitoring.setUser(userId, email, username);
    },
    []
  );

  const clearUser = useCallback(() => {
    monitoring.setUser('');
  }, []);

  return {
    setUser,
    clearUser,
  };
}
